import React from 'react'
import { Box, Chip, Paper, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import LooksOneOutlinedIcon from '@mui/icons-material/LooksOneOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import { PositionDetailInfoDto } from '../../../interfaces/Position/Position'

interface PositionDetailProps {
  item: PositionDetailInfoDto
}

const IconCircle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: '50%',
      backgroundColor: 'rgba(63, 81, 181, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: 'primary.main'
    }}
  >
    {children}
  </Box>
)

interface DetailCardProps {
  icon?: React.ReactNode
  label: string
  value?: React.ReactNode
}

const DetailCard: React.FC<DetailCardProps> = ({ icon, label, value }) => (
  <Paper
    variant='outlined'
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      borderRadius: 2,
      height: '100%'
    }}
  >
    {icon && <IconCircle>{icon}</IconCircle>}
    <Box>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='subtitle1' fontWeight={700}>
        {value ?? (
          <Typography
            component='span'
            variant='subtitle1'
            fontStyle='italic'
            color='text.disabled'
          >
            Sin información
          </Typography>
        )}
      </Typography>
    </Box>
  </Paper>
)

interface BooleanChipProps {
  value: boolean | undefined
}

const BooleanChip: React.FC<BooleanChipProps> = ({ value }) => (
  <Chip
    label={value ? 'Sí' : 'No'}
    color={value ? 'primary' : 'default'}
    size='small'
    sx={{ mt: 0.5 }}
  />
)

const SectionTitle: React.FC<{
  icon?: React.ReactNode
  children: React.ReactNode
}> = ({ icon, children }) => (
  <Typography
    variant='subtitle2'
    fontWeight={700}
    color='primary'
    sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
  >
    {icon}
    {children}
  </Typography>
)

export const PositionDetail: React.FC<PositionDetailProps> = ({ item }) => {
  const attrs = item.positionAttributes

  return (
    <Grid container spacing={2}>
      {/* Card principal: Nombre + Estado */}
      <Grid size={12}>
        <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant='caption' color='text.secondary'>
            Código de Posición
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 0.5,
              flexWrap: 'wrap'
            }}
          >
            <Typography variant='h5' fontWeight={700}>
              {item.positionCode}
            </Typography>
            <Chip
              label={item.active ? 'Activo' : 'Inactivo'}
              color={item.active ? 'primary' : 'default'}
              size='small'
            />
          </Box>
        </Paper>
      </Grid>

      {/* Info grid */}
      <Grid size={{ xs: 12, md: 6 }}>
        <DetailCard
          icon={<LocationOnOutlinedIcon />}
          label='Tipo de Posición'
          value={item.typePositionDescription}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <DetailCard
          icon={<StoreOutlinedIcon />}
          label='Zona'
          value={item.zoneName}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <DetailCard
          icon={<HomeWorkOutlinedIcon />}
          label='Almacén'
          value={item.warehouseName}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <DetailCard
          icon={<DescriptionOutlinedIcon />}
          label='Descripción'
          value={item.positionDescription || undefined}
        />
      </Grid>

      {/* Atributos físicos: una sola tarjeta con 4 columnas */}
      {(item.aisle || item.column || item.level) && (
        <>
          <Grid size={12}>
            <SectionTitle icon={<GridOnOutlinedIcon fontSize='small' />}>
              Atributos físicos
            </SectionTitle>
          </Grid>
          <Grid size={12}>
            <Paper
              variant='outlined'
              sx={{ borderRadius: 2, overflow: 'hidden' }}
            >
              <Box sx={{ display: 'flex' }}>
                {[
                  {
                    label: 'Pasillo',
                    value: item.aisle,
                    icon: <TableRowsOutlinedIcon fontSize='small' />
                  },
                  {
                    label: 'Columna',
                    value: item.column,
                    icon: <ViewColumnOutlinedIcon fontSize='small' />
                  },
                  {
                    label: 'Nivel',
                    value: item.level,
                    icon: <LayersOutlinedIcon fontSize='small' />
                  },
                  {
                    label: 'Slot',
                    value: item.slot || undefined,
                    icon: <GridViewOutlinedIcon fontSize='small' />
                  }
                ].map((attr, index, arr) => (
                  <Box
                    key={attr.label}
                    sx={{
                      flex: 1,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderRight:
                        index < arr.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <IconCircle>{attr.icon}</IconCircle>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        {attr.label}
                      </Typography>
                      <Typography variant='subtitle1' fontWeight={700}>
                        {attr.value ?? (
                          <Typography
                            component='span'
                            variant='subtitle1'
                            color='text.disabled'
                          >
                            —
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </>
      )}

      {/* Atributos operativos */}
      {attrs && (
        <>
          <Grid size={12}>
            <SectionTitle>Atributos operativos</SectionTitle>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailCard
              icon={<LooksOneOutlinedIcon />}
              label='Monosku'
              value={<BooleanChip value={attrs.monoSku} />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailCard
              icon={<LooksOneOutlinedIcon />}
              label='Monolote'
              value={<BooleanChip value={attrs.monoBatch} />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailCard
              icon={<BlockOutlinedIcon />}
              label='Bloqueo ingreso'
              value={<BooleanChip value={attrs.blockIngress} />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailCard
              icon={<BlockOutlinedIcon />}
              label='Bloqueo egreso'
              value={<BooleanChip value={attrs.blockEgress} />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailCard
              icon={<BarChartOutlinedIcon />}
              label='Capacidad máxima'
              value={attrs.maxCapacity > 0 ? attrs.maxCapacity : undefined}
            />
          </Grid>
        </>
      )}
    </Grid>
  )
}
